import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import {
  Form,
  Input,
  Radio,
  InputNumber,
  Space,
  Switch,
  Tabs,
  Divider,
} from 'antd';
import type { CategoryDetailOut, CategoryIn } from '@/api/generated/schemas';
import { ModStatus, ImageKind } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import { ImageUploadField } from '@/components/common/ImageUploadField';
import {
  CollectionDataFields,
  normalizeCollectionData,
} from '@/components/query-builder';
import { LabelsField } from '@/components/common/LabelsField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';

export const CategoryCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, form } = useForm<
    CategoryDetailOut,
    HttpError,
    CategoryIn
  >({
    resource: 'user_catalog.categories',
  });

  const handleFinish = (values: CategoryIn) => {
    return formProps.onFinish?.(normalizeCollectionData(values) as CategoryIn);
  };

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.categories.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="position"
                    label={t('user_catalog.categories.fields.position')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t('user_catalog.categories.fields.description')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <LabelsField />

                  <Space size="large">
                    <Form.Item
                      name="is_featured"
                      label={t('user_catalog.categories.fields.is_featured')}
                      valuePropName="checked"
                      rules={[{ required: true }]}
                    >
                      <Switch />
                    </Form.Item>

                    <Form.Item
                      name="is_system"
                      label={t('user_catalog.categories.fields.is_system')}
                      valuePropName="checked"
                      rules={[{ required: true }]}
                    >
                      <Switch />
                    </Form.Item>
                  </Space>

                  <ImageUploadField
                    imageKind={ImageKind.categories}
                    onUpload={(key) =>
                      form?.setFields([
                        { name: 'image_storage_key', value: key, errors: [] },
                      ])
                    }
                    label={t('user_catalog.categories.fields.image_url')}
                    required
                  />
                  <Form.Item
                    name="image_storage_key"
                    rules={[{ required: true }]}
                  >
                    <Input type="hidden" />
                  </Form.Item>

                  {/* No sections editor here: `CategoryIn` has no `sections`
                      field, so anything picked at create time is dropped.
                      They are added in edit, where they persist. */}
                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                    initialValue={ModStatus.enabled}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.categories.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.categories.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields />

                  <CollectionDataFields mode="create" />
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              forceRender: true,
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Create>
  );
};
